import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Home as HomeIcon, Cloud, Plane, Building2, MapPin, AlertCircle, Check } from "lucide-react";
import Hero from "@/components/ui/Hero";
import TrustStrip from "@/components/ui/TrustStrip";
import CTABlock from "@/components/ui/CTABlock";
import FAQAccordion from "@/components/ui/FAQAccordion";
import ServiceCard from "@/components/ui/ServiceCard";
import SwflMap from "@/components/ui/SwflMap";
import CompletedWorkGrid from "@/components/ui/CompletedWorkGrid";
import Reveal from "@/components/ui/Reveal";
import { LOCATION_BY_SLUG, LOCATIONS } from "@/data/locations";
import { SERVICE_BY_SLUG, SERVICES } from "@/data/services";

const WE_WORK_WITH_ICONS = [HomeIcon, Cloud, Plane, Building2];

const PHOTO_DISCLAIMER =
    "Project photos are examples of A1 Screening's pool cage and screen work across Southwest Florida — service type and exact location aren't stated unless confirmed.";

export default function LocationPage() {
    const { slug } = useParams();
    const location = LOCATION_BY_SLUG[slug];

    useEffect(() => {
        if (location) {
            document.title = location.meta_title;
            const meta = document.querySelector('meta[name="description"]') || (() => {
                const m = document.createElement("meta");
                m.setAttribute("name", "description");
                document.head.appendChild(m);
                return m;
            })();
            meta.setAttribute("content", location.meta_description);
        }
    }, [location]);

    if (!location) return <Navigate to="/service-areas" replace />;

    if (location.is_primary) return <NaplesView location={location} />;

    const relevant = (location.relevant_services || []).map((s) => SERVICE_BY_SLUG[s]).filter(Boolean);
    const naples = LOCATIONS.find((l) => l.is_primary);

    return (
        <>
            <Hero
                eyebrow="Service Area"
                title={location.h1}
                subtitle={location.hero}
                tertiary={{ to: "/upload-photos", label: "Have photos? Send them for technician review" }}
            />
            <TrustStrip />

            {/* Local intro + city-highlighted coverage map */}
            <section className="a1-section">
                <div className="a1-container">
                    <div className="grid lg:grid-cols-12 gap-10 items-start">
                        <Reveal className="lg:col-span-7">
                            <p className="a1-eyebrow mb-3">Local Note</p>
                            <h2 className="mb-4">How We Serve {location.name}</h2>
                            <p className="text-[var(--color-text-muted)] text-lg leading-relaxed mb-5">{location.unique_local}</p>
                            {location.local_points?.length ? (
                                <ul className="m-0 p-0 list-none space-y-3" data-testid={`loc-points-${location.slug}`}>
                                    {location.local_points.map((pt) => (
                                        <li key={pt} className="flex items-start gap-3 leading-relaxed">
                                            <Check size={18} className="text-[var(--color-accent)] shrink-0 mt-1" />
                                            <span className="text-[var(--color-text)]">{pt}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </Reveal>
                        <Reveal className="lg:col-span-5" delay={100}>
                            <SwflMap highlightSlug={location.slug} />
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="a1-section a1-bg-alt">
                <div className="a1-container">
                    <p className="a1-eyebrow mb-3">{location.name} Services</p>
                    <h2 className="mb-6">Services in {location.name}</h2>
                    <div className="grid sm:grid-cols-2 gap-5">
                        {relevant.map((s) => (
                            <Link key={s.slug} to={`/services/${s.slug}`} className="a1-card a1-card-link no-underline flex flex-col gap-3">
                                <h3 className="text-lg font-semibold leading-tight">{s.name}</h3>
                                <p className="text-sm text-[var(--color-text-muted)] leading-relaxed flex-1">{s.hero_summary}</p>
                                <span className="a1-btn-ghost mt-1">View {s.name} <ArrowRight size={14} /></span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Project examples */}
            <section className="a1-section" data-testid={`loc-photos-${location.slug}`}>
                <div className="a1-container">
                    <p className="a1-eyebrow mb-3">Project Photos</p>
                    <h2 className="mb-6">Recent A1 Screening Work</h2>
                    <CompletedWorkGrid surface="service" limit={3} />
                    <p className="a1-helper mt-5">{PHOTO_DISCLAIMER}</p>
                </div>
            </section>

            <section className="a1-section a1-bg-alt">
                <div className="a1-container max-w-3xl">
                    <p className="a1-eyebrow mb-3">Naples-Based</p>
                    <h2 className="mb-4">Why {location.name} Homeowners Choose Us</h2>
                    <p className="text-[var(--color-text-muted)] text-lg leading-relaxed mb-4">
                        We serve {location.name} from our Naples base. Family-owned, since 1999, with the same clean work and free estimates we provide every Naples customer.
                    </p>
                    <Link to={`/service-areas/${naples.slug}`} className="a1-btn-secondary">
                        <MapPin size={16} /> Based in Naples — see how we work across SWFL
                    </Link>
                </div>
            </section>

            <section className="a1-section">
                <div className="a1-container max-w-3xl">
                    <p className="a1-eyebrow mb-3">FAQ</p>
                    <h2 className="mb-6">{location.name} — Frequently Asked Questions</h2>
                    <FAQAccordion items={location.faqs} prefix={`loc-${location.slug}-faq`} />
                </div>
            </section>

            <CTABlock headline={`Get a Free Estimate in ${location.name}`} />
        </>
    );
}

function NaplesView({ location }) {
    return (
        <>
            <Hero
                eyebrow="Naples, FL · Home Base"
                title={location.h1}
                subtitle={location.hero}
                tertiary={{ to: "/upload-photos", label: "Have photos? Upload them for technician review" }}
            />
            <TrustStrip />

            {/* Naples trust band */}
            <section className="a1-section a1-bg-alt" data-testid="naples-trust-band">
                <div className="a1-container">
                    <div className="grid lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-7">
                            <p className="a1-eyebrow mb-3">Local · Naples-First</p>
                            <h2 className="mb-4">Naples Pool Cage Repair, Done Right</h2>
                            <p className="text-[var(--color-text-muted)] text-lg leading-relaxed mb-4">
                                If you live in Naples and your pool cage has a tear, a torn-out panel, storm damage, or screens that just won't keep the bugs out anymore — we'll come take a look. For free. No pressure.
                            </p>
                            <p className="text-[var(--color-text-muted)] leading-relaxed mb-5">
                                Twenty-six-plus years of doing this work in Southwest Florida means we know what holds up here and what doesn't.
                            </p>
                            <p className="text-sm font-bold uppercase tracking-widest text-[var(--color-accent)] mb-3">
                                Neighborhoods we work in
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {location.neighborhoods.map((n) => (
                                    <span key={n} className="a1-chip">
                                        <MapPin size={12} /> {n}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <SwflMap />
                        </div>
                    </div>
                </div>
            </section>

            {/* Services in Naples */}
            <section className="a1-section">
                <div className="a1-container">
                    <p className="a1-eyebrow mb-3">What We Do in Naples</p>
                    <h2 className="mb-8">Every Service We Offer — Available in Naples</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {SERVICES.map((s) => (
                            <ServiceCard key={s.slug} service={s} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Common Naples Problems */}
            <section className="a1-section a1-bg-alt">
                <div className="a1-container max-w-4xl">
                    <p className="a1-eyebrow mb-3">What We Hear From Naples Homeowners</p>
                    <h2 className="mb-6">Common Naples Screen Problems We Fix</h2>
                    <ul className="grid sm:grid-cols-2 gap-3">
                        {location.common_problems.map((p, i) => (
                            <li key={i} className="flex items-start gap-3 rounded-xl bg-[var(--color-bg-card)] border border-[var(--color-line)] p-4">
                                <AlertCircle size={20} className="text-[var(--color-storm)] shrink-0 mt-0.5" />
                                <span className="text-[var(--color-text)] leading-relaxed">{p}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* We Work With… */}
            <section className="a1-section">
                <div className="a1-container">
                    <p className="a1-eyebrow mb-3">Who We Work With</p>
                    <h2 className="mb-8">We Work With…</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        {location.we_work_with.map((card, i) => {
                            const Icon = WE_WORK_WITH_ICONS[i] || HomeIcon;
                            return (
                                <div key={i} className="a1-card a1-card-feature flex gap-4">
                                    <div className="a1-icon-wrap-lg shrink-0">
                                        <Icon size={26} />
                                    </div>
                                    <div>
                                        <h3 className="text-lg mb-2">{card.title}</h3>
                                        <p className="text-[var(--color-text-muted)] leading-relaxed">{card.body}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Project photos */}
            <section className="a1-section a1-bg-alt" data-testid="loc-photos-naples">
                <div className="a1-container">
                    <p className="a1-eyebrow mb-3">Project Photos</p>
                    <h2 className="mb-8">A1 Screening Project Photos</h2>
                    <CompletedWorkGrid surface="service" limit={3} />
                    <p className="a1-helper mt-5">{PHOTO_DISCLAIMER}</p>
                </div>
            </section>

            {/* FAQ */}
            <section className="a1-section">
                <div className="a1-container max-w-3xl">
                    <p className="a1-eyebrow mb-3">FAQ</p>
                    <h2 className="mb-6">Naples Screen Repair — Frequently Asked Questions</h2>
                    <FAQAccordion items={location.faqs} prefix={`naples-faq`} />
                </div>
            </section>

            <CTABlock headline="Get a Free Estimate in Naples" />
        </>
    );
}
